import React, { useState, useRef, useEffect } from "react";
import classes from "./Header.module.css";
import avatar from "../../Assest/image.jpg";
import { useNavigate } from "react-router-dom";

import {
    FiMenu,
    FiSearch,
    FiBell,
    FiMessageCircle,
    FiChevronDown,
    FiAlertTriangle,
    FiUser,
    FiSettings,
    FiLogOut,
    FiHelpCircle,
    FiUserPlus,
} from "react-icons/fi";

const Header = ({ toggleSidebar }) => {
    const navigate = useNavigate();
    const [showNotifications, setShowNotifications] = useState(false);
    const [showMessages, setShowMessages] = useState(false);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    
    const notificationRef = useRef(null);
    const messageRef = useRef(null);
    const profileRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
            if (messageRef.current && !messageRef.current.contains(event.target)) {
                setShowMessages(false);
            }
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            console.log("Searching for:", searchQuery);
            // Navigate to search results or filter
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("userEmail");
        navigate("/login");
    };

    const handleProfile = () => {
        navigate("/profile");
        setShowProfileMenu(false);
    };

    const handleSettings = () => {
        navigate("/settings");
        setShowProfileMenu(false);
    };

    const notifications = [
        { id: 1, title: "New patient registered", time: "5 min ago", read: false },
        { id: 2, title: "Lab results ready for John Doe", time: "15 min ago", read: false },
        { id: 3, title: "Payment received $250", time: "1 hour ago", read: true },
        { id: 4, title: "Appointment reminder for tomorrow", time: "2 hours ago", read: true },
    ];

    const messages = [
        { id: 1, sender: "Dr. Smith", message: "Can you review the lab results?", time: "10 min ago", read: false },
        { id: 2, sender: "Nurse Jane", message: "Patient in room 204 needs assistance", time: "30 min ago", read: false },
        { id: 3, sender: "Dr. Williams", message: "Surgery scheduled for 2 PM", time: "1 hour ago", read: true },
    ];

    const unreadNotifications = notifications.filter(n => !n.read).length;
    const unreadMessages = messages.filter(m => !m.read).length;

    return (
        <header className={classes.header}>
            {/* Left */}
            <div className={classes.left}>
                <button
                    className={classes.menuBtn}
                    onClick={toggleSidebar}
                    aria-label="Toggle sidebar"
                >
                    <FiMenu />
                </button>

                <form className={classes.searchBox} onSubmit={handleSearch}>
                    <FiSearch className={classes.searchIcon} />
                    <input
                        type="text"
                        placeholder="Search Patient, Staff, Invoice, Lab No. etc..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className={classes.shortcut}>/</div>
                </form>
            </div>

            {/* Right */}
            <div className={classes.right}>
                {/* Notifications */}
                <div className={classes.iconWrapper} ref={notificationRef}>
                    <div 
                        className={classes.iconBox}
                        onClick={() => setShowNotifications(!showNotifications)}
                    >
                        <FiBell />
                        {unreadNotifications > 0 && (
                            <span className={classes.badge}>{unreadNotifications}</span>
                        )}
                    </div>
                    
                    {showNotifications && (
                        <div className={classes.dropdown}>
                            <div className={classes.dropdownHeader}>
                                <h4>Notifications</h4>
                                <span className={classes.markAllRead}>Mark all as read</span>
                            </div>
                            <div className={classes.dropdownList}>
                                {notifications.map((notif) => (
                                    <div key={notif.id} className={`${classes.dropdownItem} ${!notif.read ? classes.unread : ""}`}>
                                        <div className={classes.dropdownItemContent}>
                                            <p>{notif.title}</p>
                                            <span>{notif.time}</span>
                                        </div>
                                        {!notif.read && <span className={classes.unreadDot}></span>}
                                    </div>
                                ))}
                            </div>
                            <div className={classes.dropdownFooter}>
                                <button>View All Notifications</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Messages */}
                <div className={classes.iconWrapper} ref={messageRef}>
                    <div 
                        className={classes.iconBox}
                        onClick={() => setShowMessages(!showMessages)}
                    >
                        <FiMessageCircle />
                        {unreadMessages > 0 && (
                            <span className={classes.badge}>{unreadMessages}</span>
                        )}
                    </div>
                    
                    {showMessages && (
                        <div className={classes.dropdown}>
                            <div className={classes.dropdownHeader}>
                                <h4>Messages</h4>
                                <span className={classes.markAllRead}>View all</span>
                            </div>
                            <div className={classes.dropdownList}>
                                {messages.map((msg) => (
                                    <div key={msg.id} className={`${classes.dropdownItem} ${!msg.read ? classes.unread : ""}`}>
                                        <div className={classes.dropdownItemContent}>
                                            <strong>{msg.sender}</strong>
                                            <p>{msg.message}</p>
                                            <span>{msg.time}</span>
                                        </div>
                                        {!msg.read && <span className={classes.unreadDot}></span>}
                                    </div>
                                ))}
                            </div>
                            <div className={classes.dropdownFooter}>
                                <button>View All Messages</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Emergency Button */}
                <button className={classes.emergency}>
                    <FiAlertTriangle />
                    <span>EMERGENCY</span>
                </button>

                {/* Profile */}
                <div className={classes.profile} ref={profileRef}>
                    <div 
                        className={classes.profileTrigger}
                        onClick={() => setShowProfileMenu(!showProfileMenu)}
                    >
                        <img src={avatar} alt="Profile" />
                        <div className={classes.userInfo}>
                            <h4>Admin User</h4>
                            <p>Administrator</p>
                        </div>
                        <FiChevronDown className={`${classes.arrow} ${showProfileMenu ? classes.arrowRotate : ""}`} />
                    </div>

                    {showProfileMenu && (
                        <div className={classes.profileDropdown}>
                            <div className={classes.profileDropdownHeader}>
                                <img src={avatar} alt="Profile" />
                                <div>
                                    <h4>Admin User</h4>
                                    <p>Administrator</p>
                                    <span>admin@citycare.com</span>
                                </div>
                            </div>
                            <div className={classes.profileDropdownMenu}>
                                <button onClick={handleProfile}>
                                    <FiUser />
                                    My Profile
                                </button>
                                <button onClick={handleSettings}>
                                    <FiSettings />
                                    Settings
                                </button>
                                <button>
                                    <FiHelpCircle />
                                    Help & Support
                                </button>
                                <button>
                                    <FiUserPlus />
                                    Switch Account
                                </button>
                                <div className={classes.dropdownDivider}></div>
                                <button onClick={handleLogout} className={classes.logoutBtn}>
                                    <FiLogOut />
                                    Sign Out
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;